import { useState } from "react";
import api from "@/lib/api";
import { Loader2, FileText, Search, ExternalLink } from "lucide-react";

export default function FilingsPanel({ accent }) {
  const [ticker, setTicker] = useState("AAPL");
  const [form, setForm] = useState("10-K");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = async () => {
    if (!ticker.trim()) return;
    setLoading(true); setData(null);
    try {
      const r = await api.get(`/falcon/filings/${ticker.trim().toUpperCase()}?form=${form}&limit=8`);
      setData(r.data);
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-4" data-testid="falcon-filings">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">SEC EDGAR</div>
        <h2 className="font-serif text-3xl mt-1 flex items-center gap-2"><FileText className="h-6 w-6" /> Annual & quarterly reports</h2>
        <div className="text-xs text-muted-foreground mt-1">Direct from SEC.gov — every 10-K, 10-Q, 8-K filed by US-listed companies.</div>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Ticker</label>
          <input value={ticker} onChange={(e) => setTicker(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === "Enter" && search()}
            data-testid="filings-ticker-input"
            placeholder="AAPL, MSFT, JPM..."
            className="h-9 w-40 rounded-sm border border-border bg-background px-3 text-sm font-mono" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Form type</label>
          <select value={form} onChange={(e) => setForm(e.target.value)} data-testid="filings-form-select"
            className="h-9 rounded-sm border border-border bg-background px-3 text-sm">
            <option value="10-K">10-K (Annual)</option>
            <option value="10-Q">10-Q (Quarterly)</option>
            <option value="8-K">8-K (Material event)</option>
            <option value="DEF 14A">DEF 14A (Proxy)</option>
            <option value="S-1">S-1 (IPO)</option>
            <option value="20-F">20-F (Foreign)</option>
          </select>
        </div>
        <button onClick={search} disabled={loading} data-testid="filings-search-btn"
          className="h-9 px-4 text-sm rounded-sm text-white disabled:opacity-60 inline-flex items-center gap-2"
          style={{ background: accent }}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          Search
        </button>
      </div>

      {data && (
        <div className="rounded-sm border border-border/60 bg-card p-4" data-testid="filings-results">
          {data.available ? (
            <>
              <div className="text-sm">
                <span className="text-muted-foreground">Company: </span>
                <span className="font-medium">{data.name}</span>
                <span className="text-muted-foreground ml-3">CIK: </span>
                <span className="font-mono text-xs">{data.cik}</span>
              </div>
              <div className="mt-4 divide-y divide-border/40">
                {data.filings.map((f, i) => (
                  <div key={i} className="flex items-center justify-between py-2 gap-2" data-testid={`filing-${i}`}>
                    <div>
                      <div className="font-mono text-xs">{f.form}</div>
                      <div className="text-sm">{f.filing_date}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <a href={f.url} target="_blank" rel="noreferrer"
                        className="text-xs inline-flex items-center gap-1 underline-offset-2 hover:underline">
                        Open filing <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  </div>
                ))}
                {data.filings.length === 0 && <div className="text-sm text-muted-foreground py-4">No {form} filings found.</div>}
              </div>
            </>
          ) : (
            <div className="text-sm text-muted-foreground">{data.error || "Not found."}</div>
          )}
        </div>
      )}
    </div>
  );
}
